import { PurchaseOrderSearchCondition, useSearchCondition } from '@/providers/SearchConditionProvider';
import { useEffect, useState } from 'react';
import { MultiValue } from 'react-select';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useTeamPurchaseOrderPhases } from '@/hooks/api/0260_team_purchase_order_phases';
import { SelectMulti } from '@/components/element/Form/SelectMulti';
import { useBusinessPartners } from '@/hooks/api/0430_business_partners';
import { usePaymentContexts } from '@/hooks/api/0511_payment_contexts';
import { usePaymentMethods } from '@/hooks/api/0510_payment_methods';

export const PurchaseOrderFiltersModal = (props: { id: string }) => {
  const {
    purchaseOrderDisplayType: displayType,
    purchaseOrderSearchCondition: searchCondition,
    setPurchaseOrderSearchCondition: setSearchCondition,
  } = useSearchCondition();
  const [condition, setCondition] = useState<PurchaseOrderSearchCondition>(searchCondition);

  const onTeamPurchaseOrderPhasesChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((current) => ({ ...current, team_purchase_order_phase_ids: d.map((a) => a.value) }));
  };

  const onUsersChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((current) => ({ ...current, user_ids: d.map((a) => a.value) }));
  };

  const onBusinessPartnersChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((current) => ({ ...current, business_partner_ids: d.map((a) => a.value) }));
  };

  const onPaymentMethodsChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((current) => ({ ...current, payment_method_ids: d.map((a) => a.value) }));
  };

  const onPaymentContextsChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((current) => ({ ...current, payment_context_ids: d.map((a) => a.value) }));
  };

  const onMyProjectChange = () => {
    setCondition((current) => ({ ...current, my_project: !current.my_project }));
  };

  const onArchivedChange = () => {
    setCondition((current) => ({ ...current, archived: !current.archived }));
  };

  const onApplyClick = () => {
    setSearchCondition((_) => ({ ...condition, page: 1 }));
  };

  return (
    <div
      className="modal fade"
      id={props.id}
      tabIndex={-1}
      role="dialog"
      aria-labelledby="modal-form"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-md" role="document">
        <div className="modal-content">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                フィルタ
              </h5>
              <button type="button" className="btn-close text-dark" data-bs-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="form-group">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="my-project"
                      checked={condition.my_project}
                      onChange={onMyProjectChange}
                    />
                    <label className="form-check-label" htmlFor="my-project">
                      参加済プロジェクトのみ表示
                    </label>
                  </div>
                </div>
                {displayType == 'list' && (
                  <div className="form-group">
                    <label>フェーズ</label>
                    <PhasesSelect
                      onChange={onTeamPurchaseOrderPhasesChange}
                      selectedIds={condition.team_purchase_order_phase_ids}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>担当者</label>
                  <UsersSelect onChange={onUsersChange} selectedIds={condition.user_ids} />
                </div>
                <div className="form-group">
                  <label>取引先</label>
                  <BusinessPartnersSelect
                    onChange={onBusinessPartnersChange}
                    selectedIds={condition.business_partner_ids}
                  />
                </div>
                <div className="form-group">
                  <label>支払方法</label>
                  <PaymentMethodsSelect onChange={onPaymentMethodsChange} selectedIds={condition.payment_method_ids} />
                </div>
                <div className="form-group">
                  <label>支払タイプ</label>
                  <PaymentContextsSelect
                    onChange={onPaymentContextsChange}
                    selectedIds={condition.payment_context_ids}
                  />
                </div>
                <div className="form-group">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="archived"
                      checked={condition.archived}
                      onChange={onArchivedChange}
                    />
                    <label className="form-check-label" htmlFor="archived">
                      アーカイブ済プロジェクトを含める
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                閉じる
              </button>
              <button type="button" className="btn bg-gradient-primary" data-bs-dismiss="modal" onClick={onApplyClick}>
                適用
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PhasesSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { data } = useTeamPurchaseOrderPhases({ identifier: _w_!.identifier, team_id: _t_!.id });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.map((d): SelectOptionType => ({ value: d.id, label: d.label })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const UsersSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    setOptions(_t_!.team_members.map((d): SelectOptionType => ({ value: d.user.id, label: d.user.user_account.name })));
  }, []);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const BusinessPartnersSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { data } = useBusinessPartners({ identifier: _w_!.identifier, display_type: 'purchase', page: 1, per: 1000 });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.business_partners.map((d): SelectOptionType => ({ value: d.id, label: d.display_name })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const PaymentContextsSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { data } = usePaymentContexts();
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.map((d): SelectOptionType => ({ value: d.id, label: d.label })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const PaymentMethodsSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { data } = usePaymentMethods();
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.map((d): SelectOptionType => ({ value: d.id, label: d.label })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};
